import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, MessageCircle, Activity, Hash, TrendingUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { createCategorySlug } from '../utils/urlHelpers';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl, applyPageSeo } from '../utils/seo';
import BackToTop from './BackToTop';
import CategoriesPageHero from './CategoriesPageHero';
import { buildCategoriesListSeoParagraph, latestForumUpdateIso, aggregateCategoryForums } from '../utils/categoryAggregates';
import { formatChangePercent, formatStatDelta, statDelta } from '../utils/forumStatChange';
interface Category {
  id: string;
  name: string;
  description: string;
  previous_month_forums_count: number;
}

interface CategoryStats {
  totalMembers: number;
  previousTotalMembers: number;
  totalPosts: number;
  previousTotalPosts: number;
  forumCount: number;
  previousForumCount: number;
  totalTopics: number;
  previousTotalTopics: number;
  avgScore: number;
  totalScore: number;
}

interface Props {
  darkMode: boolean;
}

const CategoriesPage: React.FC<Props> = ({ darkMode }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [forums, setForums] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState<Record<string, CategoryStats>>({});

  usePageSeo({
    title: 'Forum Kategorileri - Kategori Listesi ve İstatistikleri',
    description:
      'Türk forum sitelerinin kategorilere göre sınıflandırılması. Her kategorideki forum sayıları, üye istatistikleri ve detaylı analizler.',
    keywords:
      'forum kategorileri, forum sınıflandırması, kategori istatistikleri, forum türleri, kategori analizi',
    canonical: absoluteUrl('/category'),
  });

  useEffect(() => {
    if (loading || forums.length === 0) return;
    const agg = aggregateCategoryForums(forums);
    const desc = buildCategoriesListSeoParagraph(
      categories.length,
      agg,
      latestForumUpdateIso(forums)
    );
    applyPageSeo({
      title: 'Forum Kategorileri - Kategori Listesi ve İstatistikleri',
      description: desc.slice(0, 320),
      keywords:
        'forum kategorileri, forum sınıflandırması, kategori istatistikleri, forum türleri, kategori analizi',
      canonical: absoluteUrl('/category'),
    });
  }, [loading, forums, categories.length]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Kategorileri çek
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (categoriesError) throw categoriesError;

      // Forumları çek
      const { data: forumsData, error: forumsError } = await supabase
        .from('forums')
        .select('*')
        .eq('is_active', true);

      if (forumsError) throw forumsError;

      setCategories(categoriesData || []);
      setForums(forumsData || []);

      // İstatistikleri hesapla
      calculateCategoryStats(categoriesData || [], forumsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (forum: any): number => {
    const topicsScore = ((forum.topics_count ?? 0) * 5);
    const postsScore = ((forum.posts_count ?? 0) * 2);
    const membersScore = ((forum.members_count ?? 0) * 10);
    return Math.round((topicsScore + postsScore + membersScore) / (forum.founded_year || 1));
  };

  const calculateCategoryStats = (categories: Category[], forums: any[]) => {
    const stats: Record<string, CategoryStats> = {};

    categories.forEach(category => {
      const categoryForums = forums.filter(forum => forum.category === category.name);
      const previousMonthForums = category.previous_month_forums_count || 0;
      const totalScore = categoryForums.reduce((sum, forum) => sum + calculateScore(forum), 0);
      
      stats[category.name] = {
        totalMembers: categoryForums.reduce((sum, forum) => sum + (forum.members_count ?? 0), 0),
        previousTotalMembers: categoryForums.reduce((sum, forum) => sum + (forum.previous_month_members_count ?? 0), 0),
        totalPosts: categoryForums.reduce((sum, forum) => sum + (forum.posts_count ?? 0), 0),
        previousTotalPosts: categoryForums.reduce((sum, forum) => sum + (forum.previous_month_posts_count ?? 0), 0),
        forumCount: categoryForums.length,
        previousForumCount: previousMonthForums,
        totalTopics: categoryForums.reduce((sum, forum) => sum + (forum.topics_count ?? 0), 0),
        previousTotalTopics: categoryForums.reduce((sum, forum) => sum + (forum.previous_month_topics_count ?? 0), 0),
        avgScore: Math.round(totalScore / (categoryForums.length || 1)),
        totalScore: totalScore
      };
    });

    setCategoryStats(stats);
  };

  const deltaColor = (current: number, previous: number) => {
    const d = statDelta(current, previous);
    if (d === null || d === 0) return 'text-gray-400';
    return d > 0 ? 'text-green-500' : 'text-red-500';
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Kategorileri istatistiklere göre sırala
  const sortedCategories = categories.sort((a, b) => {
    const statsA = categoryStats[a.name];
    const statsB = categoryStats[b.name];
    if (!statsA || !statsB) return 0;
    return statsB.avgScore - statsA.avgScore;
  });

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} transition-colors duration-200`}>
        <div className="container mx-auto px-2 sm:px-4 py-8">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${darkMode ? 'border-indigo-400' : 'border-indigo-600'} mb-4`}></div>
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Kategoriler yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} transition-colors duration-200`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={20} />
          Anasayfaya Dön
        </Link>

        <header className="text-center mb-8">
          <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${darkMode ? 'text-white' : 'text-indigo-900'} mb-4`}>
            Forum Kategorileri
          </h1>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
            Türk forum sitelerinin kategorilere göre sınıflandırılması ve detaylı istatistikleri
          </p>
        </header>

        <CategoriesPageHero
          categories={categories}
          categoryStats={categoryStats}
          forums={forums}
          darkMode={darkMode}
        />

        <div className="grid gap-4 sm:gap-6">
          {sortedCategories.map((category, index) => {
            const stats = categoryStats[category.name];
            if (!stats) return null;

            return (
              <Link
                key={category.id}
                to={`/category/${createCategorySlug(category.name)}`}
                className={`block ${
                  darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                } rounded-xl p-4 sm:p-6 shadow-sm transition-all duration-200 cursor-pointer`}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-4">
                      <span className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-indigo-400' : 'text-indigo-600'} shrink-0`}>
                        #{index + 1}
                      </span>
                      <div>
                        <h2 className={`text-lg sm:text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {category.name}
                        </h2>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                          {category.description || `${category.name} kategorisindeki forumlar`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Mobil görünüm - 2x2 grid */}
                    <div className="grid grid-cols-2 gap-3 sm:hidden mb-4">
                      <div className="flex items-center gap-3">
                        <Hash className="text-purple-500 shrink-0" size={18} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Forum Sayısı</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold text-sm sm:text-base ${darkMode ? 'text-white' : ''}`}>
                              {stats.forumCount}
                            </span>
                            {formatStatDelta(stats.forumCount, stats.previousForumCount) && (
                              <span
                                className={`text-xs ${deltaColor(stats.forumCount, stats.previousForumCount)}`}
                              >
                                {formatStatDelta(stats.forumCount, stats.previousForumCount)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Users className="text-blue-500 shrink-0" size={18} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Toplam Üye</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold text-sm sm:text-base ${darkMode ? 'text-white' : ''}`}>
                              {formatNumber(stats.totalMembers)}
                            </span>
                            {formatChangePercent(stats.totalMembers, stats.previousTotalMembers) && (
                              <span
                                className={`text-xs ${deltaColor(stats.totalMembers, stats.previousTotalMembers)}`}
                              >
                                ({formatChangePercent(stats.totalMembers, stats.previousTotalMembers)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <MessageCircle className="text-purple-500 shrink-0" size={18} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Toplam Konu</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold text-sm sm:text-base ${darkMode ? 'text-white' : ''}`}>
                              {formatNumber(stats.totalTopics)}
                            </span>
                            {formatChangePercent(stats.totalTopics, stats.previousTotalTopics) && (
                              <span
                                className={`text-xs ${deltaColor(stats.totalTopics, stats.previousTotalTopics)}`}
                              >
                                ({formatChangePercent(stats.totalTopics, stats.previousTotalTopics)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Activity className="text-green-500 shrink-0" size={18} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Toplam İleti</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold text-sm sm:text-base ${darkMode ? 'text-white' : ''}`}>
                              {formatNumber(stats.totalPosts)}
                            </span>
                            {formatChangePercent(stats.totalPosts, stats.previousTotalPosts) && (
                              <span
                                className={`text-xs ${deltaColor(stats.totalPosts, stats.previousTotalPosts)}`}
                              >
                                ({formatChangePercent(stats.totalPosts, stats.previousTotalPosts)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <TrendingUp className="text-amber-500 shrink-0" size={18} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Toplam Skor</p>
                          <span className={`font-semibold text-sm sm:text-base ${darkMode ? 'text-white' : ''}`}>
                            {formatNumber(stats.totalScore)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <TrendingUp className="text-amber-600 shrink-0" size={18} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Ortalama Skor</p>
                          <span className={`font-semibold text-sm sm:text-base ${darkMode ? 'text-white' : ''}`}>
                            {formatNumber(stats.avgScore)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Desktop görünüm - tek satır */}
                    <div className="hidden sm:block">
                      {/* Toplam İstatistikler */}
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Hash className="text-purple-500 shrink-0" size={18} />
                        <div>
                          <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{stats.forumCount}</span>
                          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>forum</span>
                          {formatStatDelta(stats.forumCount, stats.previousForumCount) && (
                            <span
                              className={`ml-2 text-xs ${deltaColor(stats.forumCount, stats.previousForumCount)}`}
                            >
                              {formatStatDelta(stats.forumCount, stats.previousForumCount)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Users className="text-blue-500 shrink-0" size={18} />
                        <div>
                          <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{formatNumber(stats.totalMembers)}</span>
                          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>üye</span>
                          {formatChangePercent(stats.totalMembers, stats.previousTotalMembers) && (
                            <span
                              className={`ml-2 text-xs ${deltaColor(stats.totalMembers, stats.previousTotalMembers)}`}
                            >
                              ({formatChangePercent(stats.totalMembers, stats.previousTotalMembers)})
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <MessageCircle className="text-purple-500 shrink-0" size={18} />
                        <div>
                          <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{formatNumber(stats.totalTopics)}</span>
                          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>konu</span>
                          {formatChangePercent(stats.totalTopics, stats.previousTotalTopics) && (
                            <span
                              className={`ml-2 text-xs ${deltaColor(stats.totalTopics, stats.previousTotalTopics)}`}
                            >
                              ({formatChangePercent(stats.totalTopics, stats.previousTotalTopics)})
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Activity className="text-green-500 shrink-0" size={18} />
                        <div>
                          <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{formatNumber(stats.totalPosts)}</span>
                          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ileti</span>
                          {formatChangePercent(stats.totalPosts, stats.previousTotalPosts) && (
                            <span
                              className={`ml-2 text-xs ${deltaColor(stats.totalPosts, stats.previousTotalPosts)}`}
                            >
                              ({formatChangePercent(stats.totalPosts, stats.previousTotalPosts)})
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <TrendingUp className="text-amber-500 shrink-0" size={18} />
                        <div>
                          <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{formatNumber(stats.totalScore)}</span>
                          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>toplam</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <TrendingUp className="text-amber-600 shrink-0" size={18} />
                        <div>
                          <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{formatNumber(stats.avgScore)}</span>
                          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ortalama</span>
                        </div>
                      </div>
                      </div>
                      
                      {/* Ortalama İstatistikler */}
                      <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-blue-400" />
                          <span>Ort. Üye: {formatNumber(Math.round(stats.totalMembers / stats.forumCount))}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MessageCircle size={16} className="text-purple-400" />
                          <span>Ort. Konu: {formatNumber(Math.round(stats.totalTopics / stats.forumCount))}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Activity size={16} className="text-green-400" />
                          <span>Ort. İleti: {formatNumber(Math.round(stats.totalPosts / stats.forumCount))}</span>
                        </div>
                      </div>
                    </div>

                    {/* Mobil için özet istatistikler */}
                    {/* Mobil için alt bilgiler */}
                    <div className={`sm:hidden mt-4 pt-4 border-t border-gray-200 dark:border-gray-700`}>
                      <div className={`flex flex-wrap gap-4 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <div className="flex items-center gap-1">
                          <Users size={12} />
                          <span>Ort. Üye: {formatNumber(Math.round(stats.totalMembers / stats.forumCount))}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Activity size={12} />
                          <span>Ort. İleti: {formatNumber(Math.round(stats.totalPosts / stats.forumCount))}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageCircle size={12} />
                          <span>Ort. Konu: {formatNumber(Math.round(stats.totalTopics / stats.forumCount))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      <BackToTop darkMode={darkMode} />
    </div>
  );
};

export default CategoriesPage;